/**
 * @file inviteCode.js
 * @location src/utils/inviteCode.js
 * @role 멤버초대 코드 생성·검증·만료 헬퍼 (책임 분리 razor)
 *
 *  형식: 「INV-XXXX」 — INV 접두 + 대문자 영숫자 4자리 (혼동 글자 0/O, 1/I 제외)
 *  collision 회피: 이미 발급된 코드 목록 받아 충돌 시 재생성 (최대 50회)
 *  TTL: 7일 (SaaS razor 표준 — Slack/Linear/Notion 동일). expiresAt 저장 X (invitedAt + 7d 계산)
 */

const INVITE_TTL_DAYS = 7;
const MS_PER_DAY = 86400000;

const ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // 0/O, 1/I 제외 — 시각 혼동 방지

function randomCode() {
  let s = '';
  for (let i = 0; i < 4; i++) {
    s += ALPHABET[Math.floor(Math.random() * ALPHABET.length)];
  }
  return `INV-${s}`;
}

/**
 * 충돌 없는 새 초대 코드 생성.
 * @param {string[]} existingCodes 이미 발급된 코드 목록
 * @returns {string} 새 코드 (예: 'INV-A3F7')
 */
export function genInviteCode(existingCodes = []) {
  const used = new Set(existingCodes);
  for (let i = 0; i < 50; i++) {
    const c = randomCode();
    if (!used.has(c)) return c;
  }
  // fallback — 50회 충돌 시 타임스탬프 suffix 로 보장
  return `INV-${Date.now().toString(36).slice(-4).toUpperCase()}`;
}

/** 입력된 코드 normalize: 공백·하이픈 무시, INV 자동 보강. */
export function normalizeInviteCode(input) {
  const raw = String(input ?? '').toUpperCase().replace(/[\s-]/g, '');
  if (!raw) return '';
  if (raw.startsWith('INV')) return `INV-${raw.slice(3)}`;
  return `INV-${raw}`;
}

/** 코드 형식 정합 검증 (INV-[A-Z0-9]{4}). */
export function isValidInviteCode(code) {
  return /^INV-[A-Z2-9]{4}$/.test(code ?? '');
}

/**
 * 초대 상태 산출 — invitedAt 기준 TTL 7일.
 * @param {{ invitedAt?: string }} pending
 * @param {Date} [now]  테스트용 주입
 * @returns {{ expired: boolean, daysLeft: number, expiresAt: Date | null }}
 */
export function getInviteStatus(pending, now = new Date()) {
  if (!pending?.invitedAt) return { expired: false, daysLeft: INVITE_TTL_DAYS, expiresAt: null };
  const invitedAt = new Date(pending.invitedAt);
  if (Number.isNaN(invitedAt.getTime())) {
    return { expired: false, daysLeft: INVITE_TTL_DAYS, expiresAt: null };
  }
  const expiresAt = new Date(invitedAt.getTime() + INVITE_TTL_DAYS * MS_PER_DAY);
  const msLeft = expiresAt.getTime() - now.getTime();
  const expired = msLeft <= 0;
  const daysLeft = expired ? 0 : Math.max(1, Math.ceil(msLeft / MS_PER_DAY));
  return { expired, daysLeft, expiresAt };
}
